import * as React from "react";
import { makeStyles, Button, Input, Text } from "@fluentui/react-components";
import { Dismiss16Regular, Dismiss12Regular, Add16Regular, Person16Regular } from "@fluentui/react-icons";

var STORAGE_KEY = "agrelloSignerEmails";

interface SignersListProps {
  signers: string[];
  onChange: (signers: string[]) => void;
}

function getSavedEmails(): string[] {
  try {
    var stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // ignore parse errors
  }
  return [];
}

function saveEmail(email: string): void {
  var trimmed = email.trim().toLowerCase();
  if (trimmed.indexOf("@") === -1) return;
  var emails = getSavedEmails();
  if (emails.indexOf(trimmed) === -1) {
    emails.push(trimmed);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(emails));
  }
}

function removeStoredEmail(email: string): void {
  var emails = getSavedEmails();
  var filtered = emails.filter(function (e) {
    return e !== email;
  });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
}

const useStyles = makeStyles({
  sectionTitle: {
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: "12px",
  },
  signerRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    marginBottom: "8px",
  },
  inputWrapper: {
    flexGrow: 1,
    position: "relative",
  },
  signerInput: {
    width: "100%",
  },
  addButton: {
    marginTop: "4px",
  },
  suggestionsContainer: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "white",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.12)",
    zIndex: 10,
    maxHeight: "150px",
    overflowY: "auto",
    marginTop: "2px",
  },
  suggestionItem: {
    display: "flex",
    alignItems: "center",
    padding: "4px 8px",
    cursor: "pointer",
    columnGap: "8px",
    ":hover": {
      backgroundColor: "#f3f4f6",
    },
  },
  suggestionIcon: {
    color: "#9ca3af",
    flexShrink: 0,
  },
  suggestionText: {
    flexGrow: 1,
    fontSize: "13px",
    color: "#374151",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  suggestionRemove: {
    flexShrink: 0,
    color: "#9ca3af",
    cursor: "pointer",
    padding: "2px",
    borderRadius: "2px",
    border: "none",
    backgroundColor: "transparent",
    display: "flex",
    alignItems: "center",
    ":hover": {
      color: "#ef4444",
      backgroundColor: "#fee2e2",
    },
  },
});

const SignersList: React.FC<SignersListProps> = ({ signers, onChange }) => {
  const styles = useStyles();
  const [savedEmails, setSavedEmails] = React.useState<string[]>(getSavedEmails);
  const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);

  const handleSignerChange = (index: number, value: string) => {
    const updated = [...signers];
    updated[index] = value;
    onChange(updated);
  };

  const handleFocus = (index: number) => {
    setFocusedIndex(index);
  };

  const handleBlur = (index: number) => {
    // Delay to allow click events on suggestions to fire first
    setTimeout(function () {
      setFocusedIndex(function (current) {
        return current === index ? null : current;
      });
    }, 150);
    var email = signers[index];
    if (email && email.trim().indexOf("@") !== -1) {
      saveEmail(email);
      setSavedEmails(getSavedEmails());
    }
  };

  const handleSelectSuggestion = (index: number, email: string) => {
    handleSignerChange(index, email);
    setFocusedIndex(null);
    saveEmail(email);
    setSavedEmails(getSavedEmails());
  };

  const handleRemoveSuggestion = (email: string, event: React.MouseEvent) => {
    event.stopPropagation();
    removeStoredEmail(email);
    setSavedEmails(getSavedEmails());
  };

  const addSigner = () => {
    onChange([...signers, ""]);
  };

  const removeSigner = (index: number) => {
    if (signers.length <= 1) return;
    const updated = signers.filter((_, i) => i !== index);
    onChange(updated);
  };

  const getSuggestions = (index: number): string[] => {
    var currentValue = signers[index].trim().toLowerCase();
    var currentEmails = signers.map(function (s, i) {
      return i !== index ? s.trim().toLowerCase() : "";
    });
    return savedEmails.filter(function (email) {
      // Exclude emails already used in other signer fields
      if (currentEmails.indexOf(email) !== -1) return false;
      // If user is typing, filter by what they've typed
      if (currentValue && email.indexOf(currentValue) === -1) return false;
      // Don't show if it exactly matches current value
      if (email === currentValue) return false;
      return true;
    });
  };

  return (
    <div>
      <Text className={styles.sectionTitle} block>
        Signers
      </Text>
      {signers.map((signer, index) => {
        var suggestions = focusedIndex === index ? getSuggestions(index) : [];
        return (
          <div key={index} className={styles.signerRow}>
            <div className={styles.inputWrapper}>
              <Input
                className={styles.signerInput}
                value={signer}
                onChange={(_, data) => handleSignerChange(index, data.value)}
                onFocus={() => handleFocus(index)}
                onBlur={() => handleBlur(index)}
                placeholder="email@example.com"
              />
              {suggestions.length > 0 && (
                <div className={styles.suggestionsContainer}>
                  {suggestions.map(function (email) {
                    return (
                      <div
                        key={email}
                        className={styles.suggestionItem}
                        onMouseDown={function (e) {
                          e.preventDefault();
                          handleSelectSuggestion(index, email);
                        }}
                      >
                        <Person16Regular className={styles.suggestionIcon} />
                        <span className={styles.suggestionText}>{email}</span>
                        <button
                          className={styles.suggestionRemove}
                          onMouseDown={function (e) {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveSuggestion(email, e as any);
                          }}
                          aria-label={"Remove " + email}
                        >
                          <Dismiss12Regular />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <Button
              size="small"
              appearance="subtle"
              icon={<Dismiss16Regular />}
              onClick={() => removeSigner(index)}
              disabled={signers.length <= 1}
              aria-label="Remove signer"
            />
          </div>
        );
      })}
      <Button className={styles.addButton} size="small" appearance="subtle" icon={<Add16Regular />} onClick={addSigner}>
        Add signer
      </Button>
    </div>
  );
};

export default SignersList;
